import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Suspense, useState, useMemo } from 'react'
import { z } from 'zod'
import { Card, CardHeader, CardTitle, CardContent } from '@/src/components/ui/card'
import { OrientationOverlay } from '@/src/components/OrientationOverlay'
import { listDrawings } from '@/src/api/drawing'
import { listProjects } from '@/src/api/project'
import { Bar, BarChart, XAxis } from 'recharts'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/src/components/ui/chart'
import { usageStats } from '@/src/api/log'
import { Spinner } from '@/src/components/ui/spinner'
import { Button } from '@/src/components/ui/button'
import { Badge } from '@/src/components/ui/badge'
import { ImageIcon, FolderSimpleIcon, ChartLineUpIcon, PlusIcon, ClockIcon, BellIcon, CheckCircleIcon, UserIcon } from '@phosphor-icons/react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { getPendingApprovals } from '@/src/api/user'
import { confirmProjectAccess } from '@/src/api/file-duplicate-check'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/src/components/AuthContextProvider'
import { PERMISSIONS } from '@/permission.config'
import { useTranslation } from '@/src/i18n/client'
import { DateTimeText } from '@/src/components/ui/date-time'
import { formatDayLabel, formatWeekLabel } from '@/src/components/ui/date-time'
import { CreateAction } from '@/src/components/manage/actions'

const searchSchema = z.object({
  period: z.string().optional(),
})

export const Route = createFileRoute('/_manage/manage/dashboard/')({
  component: DashboardPage,
  validateSearch: searchSchema,
})

type Period = {
  label: string
  key: string
  stats: { buckets: { label: string; count: number }[] }
}

function StatCard({
  title,
  value,
  icon: Icon,
  action
}: {
  title: string
  value: number | string
  icon: React.ComponentType<{ className?: string }>
  action?: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {action && <div className="mt-2">{action}</div>}
      </CardContent>
    </Card>
  )
}

function UsageChart({ periods, selected, onSelect }: { periods: Period[]; selected: string; onSelect: (period: string) => void }) {
  const { t, i18n } = useTranslation('manage')
  const period = periods.find(p => p.key === selected) || periods[1]
  const isDay = period.key === '24h'

  const chartData = useMemo(() => {
    const locale = i18n.language || 'en'
    const data = [...period.stats.buckets].reverse().map((b: any) => {
      const d = new Date(b.label)
      const dateLabel = isDay
        ? !Number.isNaN(d.getTime())
          ? `${String(d.getHours()).padStart(2, '0')}:00`
          : (() => {
              const hourMatch = String(b.label).match(/\b(\d{1,2})(?::\d{2})?\b/)
              return hourMatch ? `${hourMatch[1].padStart(2, '0')}:00` : String(b.label)
            })()
        : period.key === '7d'
          ? formatDayLabel(d, locale)
          : period.key === '30d'
            ? formatWeekLabel(d, locale)
            : d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')

      return {
        date: dateLabel,
        value: b.count,
      }
    })

    if (period.key === '30d') {
      const weekMap: Record<string, number> = {}
      data.forEach(d => {
        weekMap[d.date] = (weekMap[d.date] || 0) + d.value
      })
      return Object.entries(weekMap).map(([date, value]) => ({ date, value }))
    }
    return data
  }, [period, isDay, i18n.language])

  return (
    <div className="w-full">
      <div className="mb-2 flex gap-2 justify-start w-full">
        {periods.map(p => (
          <button
            key={p.key}
            className={`px-2 py-1 rounded ${selected === p.key ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
            onClick={() => onSelect(p.key)}
            type="button"
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="w-full" style={{ height: '192px' }}>
        <ChartContainer
          config={{
            value: {
              label: t('dashboard.requests'),
              color: "hsl(var(--chart-1))",
            },
          }}
          className="h-full w-full"
        >
          <BarChart data={chartData}>
            <Bar dataKey="value" fill="var(--color-value)" radius={4} isAnimationActive={false} />
            <ChartTooltip content={<ChartTooltipContent />} cursor={false} animationDuration={0} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10 }}
              interval={0}
              angle={isDay ? 0 : -45}
              textAnchor={isDay ? 'middle' : 'end'}
              tickMargin={14}
              height={isDay ? 44 : 72}
              reversed
            />
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  )
}

function DashboardPage() {
  const { t } = useTranslation('manage')
  const queryClient = useQueryClient()
  const { permissions, isAdmin } = useAuth()
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()
  const selectedPeriod = searchParams.period ?? '7d'
  const setSelectedPeriod = (nextPeriod: string) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        period: nextPeriod === '7d' ? undefined : nextPeriod,
      }),
    })
  }

  // Check if user has project permissions
  const hasProjectPermissions = isAdmin || permissions.includes(PERMISSIONS.PROJECTS_LIST_ALL)

  // Fetch pending access approvals
  const { data: pendingApprovals } = useQuery({
    queryKey: ['pendingApprovals'],
    queryFn: () => getPendingApprovals(),
    enabled: hasProjectPermissions, // Only fetch if user has project permissions
  })

  const approveMutation = useMutation({
    mutationFn: (token: string) => confirmProjectAccess({ data: { token } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] })
      toast.success(t('dashboard.accessGranted'))
    },
    onError: (err: Error) => toast.error(err.message || t('dashboard.failedToApprove')),
  })

  // Fetch drawings
  const { data: drawingsData, error: errorDrawings, isLoading: isLoadingDrawings } = useQuery({
    queryKey: ['drawings', 'list', { limit: 5 }],
    queryFn: () => listDrawings({ data: { limit: 5, order: 'desc' } }),
  })

  // Fetch projects
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['projects', 'list'],
    queryFn: () => listProjects(),
    enabled: hasProjectPermissions, // Only fetch if user has project permissions
  })

  // Fetch usage stats
  const { data: usageStats30d } = useQuery({
    queryKey: ['log', 'usageStats', '30d'],
    queryFn: () => usageStats({ data: { period: { value: 30, unit: 'days' } } }),
  })

  const { data: usageStats7d } = useQuery({
    queryKey: ['log', 'usageStats', '7d'],
    queryFn: () => usageStats({ data: { period: { value: 7, unit: 'days' } } }),
  })

  const { data: usageStats1d } = useQuery({
    queryKey: ['log', 'usageStats', '24h'],
    queryFn: () => usageStats({ data: { period: { value: 24, unit: 'hours' } } }),
  })

  const drawings = drawingsData?.drawings || []
  const projects = (projectsData || []).slice(0, 5) // Take only first 5
  const totalDrawings = drawingsData?.total || 0
  const totalProjects = projectsData?.length || 0

  const periods = [
    { label: t('dashboard.period24h'), key: '24h', stats: usageStats1d || { buckets: [] } },
    { label: t('dashboard.period7d'), key: '7d', stats: usageStats7d || { buckets: [] } },
    { label: t('dashboard.period30d'), key: '30d', stats: usageStats30d || { buckets: [] } },
  ]

  // Calculate total usage across the selected period
  const totalUsage = periods.reduce((sum, period) => {
    return sum + period.stats.buckets.reduce((pSum, bucket) => pSum + bucket.count, 0)
  }, 0)

  return (
    <>
      <OrientationOverlay />
      <div className="bg-background text-foreground">
        <AdminToolbar>
          <AdminToolbar.Title className="text-lg font-semibold mb-1 md:mb-0">{t('dashboard.title')}</AdminToolbar.Title>
          <AdminToolbar.Right>
            <CreateAction to="/upload" label={t('dashboard.uploadDrawing')} />
          </AdminToolbar.Right>
        </AdminToolbar>

        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <StatCard
              title={t('dashboard.totalDrawings')}
              value={totalDrawings}
              icon={ImageIcon}
              action={
                <Link to="/manage/drawings">
                  <Button variant="link" size="sm" className="p-0 h-auto">
                    {t('dashboard.viewAll')} →
                  </Button>
                </Link>
              }
            />
            {hasProjectPermissions && (
              <StatCard
                title={t('dashboard.totalProjects')}
                value={totalProjects}
                icon={FolderSimpleIcon}
                action={
                  <Link to="/manage/projects">
                    <Button variant="link" size="sm" className="p-0 h-auto">
                      {t('dashboard.viewAll')} →
                    </Button>
                  </Link>
                }
              />
            )}
            <StatCard
              title={t('dashboard.apiRequests')}
              value={totalUsage}
              icon={ChartLineUpIcon}
            />
          </div>

          {/* Usage Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ChartLineUpIcon className="h-5 w-5" />
                {t('dashboard.usageActivity')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Suspense fallback={<Spinner size={48} />}>
                <UsageChart periods={periods} selected={selectedPeriod} onSelect={setSelectedPeriod} />
              </Suspense>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          {/* Pending Approvals */}
          {hasProjectPermissions && pendingApprovals && pendingApprovals.length > 0 && (
            <Card className="border-amber-200 dark:border-amber-900">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <BellIcon className="h-4 w-4 text-amber-500" weight="fill" />
                  {t('dashboard.pendingApprovals')}
                  <Badge variant="secondary" className="ml-1">{pendingApprovals.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingApprovals.map((approval: any) => (
                    <div key={approval.id} className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <UserIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {approval.requestingUser.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t('dashboard.wantsAccessTo')} <strong>{approval.projectName}</strong>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2 shrink-0">
                        <span className="text-xs text-muted-foreground">
                          {approval.expiresAt ? <>{t('dashboard.expires')} <DateTimeText>{approval.expiresAt}</DateTimeText></> : ''}
                        </span>
                        <Button
                          size="sm"
                          onClick={() => approveMutation.mutate(approval.token)}
                          disabled={approveMutation.isPending}
                          className="h-7 text-xs"
                        >
                          <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                          {t('dashboard.approve')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ClockIcon className="h-5 w-5" />
              {t('dashboard.recentActivity')}
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              {/* Recent Drawings */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">{t('dashboard.recentDrawings')}</CardTitle>
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {isLoadingDrawings ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner size={48} />
                    </div>
                  ) : errorDrawings ? (
                    <p className="text-sm text-destructive py-4">{t('dashboard.failedToLoadDrawings')}</p>
                  ) : drawings.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-muted-foreground mb-3">{t('dashboard.noDrawingsYet')}</p>
                      <Link to="/upload">
                        <Button size="sm">
                          <PlusIcon className="w-4 h-4 mr-2" />
                          {t('dashboard.uploadDrawing')}
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {drawings.map((drawing: any) => (
                        <Link
                          key={drawing.id}
                          to="/results"
                          search={{ shareId: drawing.shareId }}
                          className="block border-b pb-2 last:border-0 hover:bg-muted/50 rounded transition-colors"
                        >
                          <div className="text-sm font-medium truncate">
                            {drawing.name || t('dashboard.untitled')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            <DateTimeText>{drawing.createdAt}</DateTimeText>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Projects */}
              {hasProjectPermissions && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">{t('dashboard.recentProjects')}</CardTitle>
                    <FolderSimpleIcon className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    {isLoadingProjects ? (
                      <div className="flex items-center justify-center py-8">
                        <Spinner size={48} />
                      </div>
                    ) : projects.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-muted-foreground mb-3">{t('dashboard.noProjectsYet')}</p>
                        <Link to="/manage/projects">
                          <Button size="sm">
                            <PlusIcon className="w-4 h-4 mr-2" />
                            {t('dashboard.newProject')}
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {projects.map((project: any) => (
                          <Link
                            key={project.id}
                            to="/manage/projects/$id"
                            params={{ id: project.id }}
                            search={{ edit: false }}
                            className="block border-b pb-2 last:border-0 hover:bg-muted/50 rounded transition-colors"
                          >
                            <div className="text-sm font-medium truncate">
                              {project.name || t('dashboard.untitled')}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              <DateTimeText>{project.createdAt}</DateTimeText>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
